import SaleItem from "./saleItem";
import EDocument from "./edocument";
import store from "../store";
import Big from "big.js";
import { computeTaxTotals, baseAtRate } from "./taxCatalog";

const CreditNote = class {
    constructor(object) {
        if (object === undefined) {
            this.id = null;
            this.saleId = null;
            this.reason = null;
            this.customer = null;
            this.saleSerialNumber = null;
            this.reason = null;
            this.base0 = new Big('0.0');
            this.base12 = new Big('0.0');
            this.tax = new Big('0.0');
            // Desglose por tarifa (fuente de sub5 / subGeneral en la caja de totales).
            this.taxBuckets = [];
            this.iceValue = new Big('0.0');
            this.taxPercentage = store.getters.business.taxPercentage;
            this.createdAt = null;
            this.items = [];
            this.edoc = null;
            this.redemption = null;
        } else {
            this.id = object.id;
            this.saleId = object.saleId;
            this.reason = object.reason;
            this.customer = object.customer;
            this.saleSerialNumber = object.saleSerialNumber;
            this.reason = object.reason;
            this.base0 = (object.base0 !== undefined) ? new Big(object.base0) : new Big('0.0');
            this.base12 = (object.base12 !== undefined) ? new Big(object.base12) : new Big('0.0');
            this.tax = (object.tax !== undefined) ? new Big(object.tax) : new Big('0.0');
            this.taxBuckets = Array.isArray(object.taxLines) ? object.taxLines : [];
            this.iceValue = (object.iceValue !== undefined && object.iceValue !== null) ? new Big(object.iceValue) : new Big('0.0');
            this.taxPercentage = object.taxPercentage || store.getters.business.taxPercentage;
            this.createdAt = new Date(Date.parse(object.createdAt));
            
            // Parse items
            if (object.items !== undefined && object.items !== null && object.items.length > 0) { 
                this.items = object.items.map(item => new SaleItem(item)); 
            }

            // Parse edoc if exists
            if (object.edoc !== undefined && object.edoc !== null) {
                this.edoc = new EDocument(object.edoc);
            } else { this.edoc = null }

            // Canje: la venta que pagó con esta nota ({saleId, saleSerialNumber}),
            // o null si el saldo a favor sigue disponible.
            this.redemption = object.redemption || null;
        }
    }

    get isRedeemed() {
        return this.redemption !== null && this.redemption !== undefined;
    }

    // Secuencial del comprobante, o el número interno si todavía no tiene.
    get label() {
        if (this.edoc !== null && this.edoc !== undefined) { return this.edoc.sequence }
        return "#" + String(this.id).padStart(8, "0");
    }

    get redemptionReadable() {
        if (!this.isRedeemed) { return null }
        const sale = this.redemption.saleSerialNumber
            ? "factura " + this.redemption.saleSerialNumber
            : "venta #" + String(this.redemption.saleId).padStart(8, "0");
        return "Canjeada en la " + sale;
    }

    // El ICE suma al total igual que en Sale/Proforma: es el mismo invariante
    // base0 + base12 + iceValue + tax que documenta backend.models.CreditNote y
    // que emite el XML en <valorModificacion> (SignatureManager.py).
    get total() {
        return this.base0.plus(this.base12.round(2)).plus(this.iceValue.round(2)).plus(this.tax.round(2)).round(2);
    }

    get createdAtFormatted() {
        const options = { year: 'numeric', month: 'numeric', day: 'numeric'};
        return this.createdAt.toLocaleDateString('es-EC', options) + " - " + this.createdAt.toLocaleTimeString('en-US');
    }

   get edocState() {
        if (this.edoc === null) { return "PENDIENTE" }
        if (this.edoc.nulled === true) { return "ANULADA" }
        else if (this.isRedeemed) { return "CANJEADA" }
        else if (this.edoc.authorized === true) { return "AUTORIZADO" }
        else if (this.edoc.received === true) { return "POR AUTORIZAR" }
        else { return "POR REPORTAR" }
    }
    get edocStateBadgeColor() {
        if (this.edoc === null) { return "gray" }
        if (this.edoc.nulled === true) { return "red" }
        else if (this.isRedeemed) { return "blue" }
        else if (this.edoc.authorized === true) { return "green" }
        else if (this.edoc.received === true) { return "yellow" }
        else { return "orange" }
    }

    updateItems(item, append=true) {
        if (append) { this.items.unshift(item) }
        else {
            let index = this.getItemIndex(item);
            if (index === -1) { this.items.unshift(item) } 
            else { this.items[index] = item }
        }
        this.updateValues();
     }
     deleteItem(item) {
        let index = this.getItemIndex(item);
        this.items.splice(index, 1);
        if (this.items.length == 0) { this.items = [] }
        this.updateValues();
    }
    getItemIndex(item) {
        var index = -1;
        if (item.uid === null || this.items === null || this.items === undefined) { return -1; }
        for (var i=0; i<this.items.length; i++) {
            if (this.items[i].uid === item.uid) { return i; }
        }
        return index;
    }

    // Desglose por tarifa para la caja de totales. Se muestra SIEMPRE una fila
    // por tarifa (0% / 5% / general) aunque esté en cero: es el formato estándar
    // del SRI y el mismo que emite el RIDE. Agrupar 5% y 15% en un solo
    // "subtotal gravado" oculta información que el comprobante sí detalla.
    get sub5() {
        return baseAtRate(this.taxBuckets, '5');
    }

    // Todo lo gravado que NO es 5%. Se calcula por resta para que ninguna base
    // desaparezca de la vista (p.ej. un documento histórico al 12% o al 14%).
    // Etiqueta de la fila de tarifa general. Usa la tarifa del DOCUMENTO (que en
    // un comprobante histórico puede ser 12% o 14%, no la vigente) y cae a la del
    // negocio si viniera vacía, para no mostrar nunca un "Subtotal %" sin número.
    get subGeneralLabel() {
        const rate = this.taxPercentage || store.getters.business.taxPercentage || '15';
        return 'Subtotal ' + rate + '%';
    }

    get subGeneral() {
        return this.base12.minus(this.sub5);
    }

    updateValues() {
        var discount_ = new Big('0.0');
        var taxLines = [];
        for (var i=0; i<this.items.length; i++) {
            var base = this.items[i].amount.times(this.items[i].unitPrice).times(this.items[i].discount.times(-1).plus(1));
            // ICE: si el item trae su producto, se recalcula para que no quede
            // desactualizado al cambiar la cantidad; si no (item ya persistido),
            // se usa el monto congelado que devolvió el backend.
            var prod = this.items[i].productObj;
            var itemIce = (prod && prod.hasIce && prod.estimateIce)
                ? prod.estimateIce(this.items[i].amount, base)
                : this.items[i].iceValue;
            taxLines.push({ ivaCode: this.items[i].ivaCode, base: base,
                            iceValue: itemIce, iceCode: this.items[i].iceCode });
            let itemDiscount = this.items[i].amount.times(this.items[i].unitPrice).times(this.items[i].discount);
            discount_ = discount_.plus(itemDiscount);
        }

        // IVA agregado POR TARIFA (ver computeTaxTotals, espejo del backend).
        var totals = computeTaxTotals(taxLines);
        this.taxBuckets = totals.buckets;
        this.base0 = totals.sub0;
        this.base12 = totals.sub12;
        this.tax = totals.tax;
        this.iceValue = totals.iceTotal;
        this.discount = discount_;
    }
}
export default CreditNote